import { Table2, Columns3, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SegmentedButton } from '@/components/ui/atoms/SegmentedButton';
import { createGTMTracking, getGTMAttributes } from '@/utils/gtm/gtm-tracking';

export type CandidatesPipelineView = 'table' | 'kanban' | 'dashboard';

/** Views remembered per job; the charts view is always temporary. */
export const PERSISTED_CANDIDATES_VIEWS = ['table', 'kanban'] as const;

interface CandidatesViewSwitcherProps {
  value: CandidatesPipelineView;
  onValueChange: (value: CandidatesPipelineView) => void;
}

export function CandidatesViewSwitcher({
  value,
  onValueChange,
}: CandidatesViewSwitcherProps) {
  const { t } = useTranslation(['job']);

  const tableLabel = t('candidates.toggle_table');
  const kanbanLabel = t('candidates.toggle_kanban', { defaultValue: 'Kanban' });
  const dashboardLabel = t('candidates.toggle_chart');

  return (
    <SegmentedButton
      aria-label={t('candidates.view_switcher_aria', {
        defaultValue: 'Candidate pipeline view',
      })}
      size="md"
      appearance="raised"
      intent="secondary"
      value={value}
      onValueChange={(next) => {
        if (next === 'table' || next === 'kanban' || next === 'dashboard') {
          onValueChange(next);
        }
      }}
      options={[
        {
          value: 'table',
          icon: <Table2 />,
          title: tableLabel,
          dataAttributes: getGTMAttributes(
            createGTMTracking('SWITCH_CANDIDATE_VIEW', {
              origin: 'candidates_toolbar',
              target: 'table',
            })
          ),
        },
        {
          value: 'kanban',
          icon: <Columns3 />,
          title: kanbanLabel,
          dataAttributes: getGTMAttributes(
            createGTMTracking('SWITCH_CANDIDATE_VIEW', {
              origin: 'candidates_toolbar',
              target: 'kanban',
            })
          ),
        },
        {
          value: 'dashboard',
          icon: <BarChart3 />,
          title: dashboardLabel,
          dataAttributes: getGTMAttributes(
            createGTMTracking('SWITCH_CANDIDATE_VIEW', {
              origin: 'candidates_toolbar',
              target: 'dashboard',
            })
          ),
        },
      ]}
    />
  );
}
